<template>

    <div class="login-screen">
      <div class="login-card">
        <AppAuthBrand
        v-if="step < 3"
          :data="brandData"
          :logoAlt="companyName"
          subtitle-size="16px"
          title-color="#151a24"
          subtitle-color="#64748b"
          gap="10px"
        />

      
        <!-- Step 1: Details -->
        <v-form v-if="step === 1" class="signup-form">
          <AppTextField v-model="fullName" type="text" autocomplete="name" prepend-inner-icon="mdi-account-outline"
            placeholder="Full name" class="auth-input" :error-messages="nameError"
            @update:modelValue="nameError = ''" />

          <AppTextField v-model="email" type="email" autocomplete="email" prepend-inner-icon="mdi-email-outline"
            placeholder="Email address" class="auth-input text-lowercase" :error-messages="emailError"
            @update:modelValue="emailError = ''" />

          <AppTextField v-model="businessName" type="text" autocomplete="name" prepend-inner-icon="mdi-domain"
            placeholder="Business name" class="auth-input" :error-messages="businessNameError"
            @update:modelValue="businessNameError = ''" />
        
          <AppTextField v-model="businessEmail" type="text" autocomplete="name" prepend-inner-icon="mdi-email"
            placeholder="Business email" class="auth-input" :error-messages="businessEmailError"
            @update:modelValue="businessEmailError = ''" />


          <AppTextField v-model="password" :type="showPassword ? 'text' : 'password'"
            autocomplete="new-password" prepend-inner-icon="mdi-lock-outline"
            :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              " placeholder="Password" class="auth-input" :error-messages="passwordError"
            @update:modelValue="passwordError = ''" @click:append-inner="showPassword = !showPassword" />

          <AppTextField v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
            autocomplete="new-password" prepend-inner-icon="mdi-lock-check-outline"
            :append-inner-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              " placeholder="Confirm password" class="auth-input" :error-messages="confirmPasswordError"
            @update:modelValue="confirmPasswordError = ''"
            @click:append-inner="showConfirmPassword = !showConfirmPassword" />

          <AppButtonFlat @click="handleSubmit" :loading="isSubmitting" :disabled="isSubmitting">
            Create account
          </AppButtonFlat>

          <p class="signup-text">
            Already have an account?
            <button type="button" class="signup-link" @click="router.push({ name: 'login.page' })">
              Sign In
            </button>
          </p>
        </v-form>

        <!-- Step 2: OTP Verification -->
        <div v-else-if="step === 2">
          <v-form class="signup-form">
            <AppOTPField
              v-model="otp"
              :length="6"
              type="number"
              :error-message="otpError"
              @update:modelValue="otpError = ''"
              @finish="handleVerifyOtp"
            />
          </v-form>

          <div class="mt-2">
            <AppButtonFlat block @click="handleVerifyOtp" :loading="isVerifyingOtp" :disabled="isVerifyingOtp">
              Verify OTP
            </AppButtonFlat>
          </div>
        </div>

        <!-- Step 3: Verified -->
        <div v-else class="verified">
          <div class="verified__icon">
            <v-icon size="34">mdi-check-circle</v-icon>
          </div>
          <h3 class="verified__title">Email verified</h3>
          <p class="verified__subtitle">
            {{ email }} has been verified successfully.
          </p>

          <div class="mt-2">
            <AppButtonFlat block @click="router.push({ name: 'vendor.dashboard' })">
              Go to Dashboard
            </AppButtonFlat>
          </div>
        </div>
      </div>
    </div>

</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useAppStore } from '@/shared/stores/app';
import AppAuthBrand from "@/modules/auth/components/AppAuthBrand.vue";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppOTPField from "@/shared/components/ui/AppOTPField.vue";
import { signup, passwordVerifyOTP, saveAuthToken, saveActiveContext } from "@/modules/auth/services/auth.api";
import { setContexts, setCustomer } from "@/modules/auth/services/auth.storage";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import fallbackLogo from '@/assets/s-logo.png';
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

const step = ref<1 | 2 | 3>(1);
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const fullName = ref("");
const email = ref("");
const businessName = ref("");
const businessEmail = ref("");
const password = ref("");
const confirmPassword = ref("");
const otp = ref("");
const router = useRouter();
const nameError = ref("");
const emailError = ref("");
const businessNameError = ref("");
const businessEmailError = ref("");
const passwordError = ref("");
const confirmPasswordError = ref("");
const otpError = ref("");
const isSubmitting = ref(false);
const isVerifyingOtp = ref(false);
const appStore = useAppStore();
const snackbar = useSnackbarStore();
const companyName = computed(() =>
  (appStore.company?.name || appStore.appName || "GlobalApp").toString().trim(),
);
const logo = computed(() => appStore.company?.logoUrl || fallbackLogo);
const subtitle = computed(() => {
  if (step.value === 1) return "Create your account.";
  if (step.value === 2) return `Enter the code sent to ${email.value || "your email"}.`;
  return "Your email has been verified.";
});
const brandData = computed(() => ({
  logo: logo.value,
  title: companyName.value,
  subtitle: subtitle.value,
}));

function validateInputs() {
  nameError.value = "";
  emailError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";

  if (!fullName.value.trim()) {
    nameError.value = "Full name is required";
  }

  if (!email.value.trim()) {
    emailError.value = "Email is required";
  } else if (!/.+@.+\..+/.test(email.value)) {
    emailError.value = "Enter a valid email address";
  }

  if (!businessName.value.trim()) {
    businessNameError.value = "Company name is required";
  }

  if (!businessEmail.value.trim()) {
    businessEmailError.value = "Company email is required";
  } else if (!/.+@.+\..+/.test(businessEmail.value)) {
    businessEmailError.value = "Enter a valid email address";
  }

  if (!password.value) {
    passwordError.value = "Password is required";
  } else if (password.value.length < 6) {
    passwordError.value = "Minimum 6 characters";
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = "Confirm your password";
  } else if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = "Passwords do not match";
  }

  return (
    !nameError.value &&
    !emailError.value &&
    !businessNameError.value &&
    !businessEmailError.value &&
    !passwordError.value &&
    !confirmPasswordError.value
  );
}

async function handleSubmit() {
  if (!validateInputs()) return;
  try {
    isSubmitting.value = true;

    // Prepare payload but avoid logging sensitive password value
    const payload = {
      full_name: fullName.value,
      email: email.value,
      business_name: businessName.value,
      business_email: businessEmail.value,
      // password intentionally omitted from debug log
    };
    console.info('[signup-vendor] submitting signup payload (password omitted):', payload);

    const response = await signup({
      full_name: fullName.value,
      email: email.value,
      business_name: businessName.value,
      business_email: businessEmail.value,
      password: password.value,
      password_confirmation: confirmPassword.value,
    });


    // Normalize common response shapes and persist auth info
    const data = response?.data ?? response;

    const token = data?.token || data?.access_token || response?.token || response?.access_token;
    if (token) saveAuthToken(token);

    const activeContext = data?.active_context || response?.active_context || data?.data?.active_context;
    if (activeContext) saveActiveContext(activeContext);

    if (Array.isArray(data?.contexts)) {
      try { setContexts(data.contexts); } catch (_e) { /* ignore */ }
    }

    const client = data?.client || data?.customer || data;
    if (client && (client.id || client.email || client.first_name || client.name)) {
      const customerForStorage = {
        id: client?.id ?? client?.user_id ?? 0,
        first_name: client?.first_name ?? client?.fname ?? client?.name ?? "",
        last_name: client?.last_name ?? client?.lname ?? "",
        email: client?.email ?? "",
        phone: client?.phone ?? "",
        avatar: client?.avatar ?? "",
        first_login: typeof client?.first_login === "boolean" ? client.first_login : undefined,
      };
      try { setCustomer(customerForStorage); } catch (_e) { /* ignore */ }
    }

    password.value = "";
    confirmPassword.value = "";
    passwordError.value = "";
    confirmPasswordError.value = "";
    otp.value = "";
    otpError.value = "";
    step.value = 2;
    snackbar.success("OTP sent to your email.");
  } catch (error) {
    // Enhanced debug output for signup failures
    try {
      console.error('[signup-vendor] Signup failed:', error);
      if (error && typeof error === 'object') {
        // axios-style error
        const resp = (error as any).response;
        if (resp) {
          console.error('[signup-vendor] response status:', resp.status);
          console.error('[signup-vendor] response headers:', resp.headers);
          console.error('[signup-vendor] response data:', resp.data);
        } else {
          console.error('[signup-vendor] error message:', (error as any).message || error);
        }
      }
    } catch (logErr) {
      console.error('[signup-vendor] Failed to log error details', logErr);
    }

    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Signup failed";

    if (errors) {
      if (errors.full_name?.length) nameError.value = errors.full_name[0];
      if (errors.name?.length) nameError.value = errors.name[0];
      if (errors.business_name?.length) businessNameError.value = errors.business_name[0];
      if (errors.business_email?.length) businessEmailError.value = errors.business_email[0];
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.password?.length) passwordError.value = errors.password[0];
      if (errors.password_confirmation?.length)
        confirmPasswordError.value = errors.password_confirmation[0];
    }

    if (!nameError.value && !emailError.value && !passwordError.value && !confirmPasswordError.value) {
      passwordError.value = message;
    }
  } finally {
    isSubmitting.value = false;
  }
}

function validateOtp(length = 6) {
  otpError.value = "";
  const raw = otp.value?.toString().trim() || "";

  if (!raw) {
    otpError.value = "OTP is required";
    return false;
  }

  if (!/^\d+$/.test(raw)) {
    otpError.value = "OTP must be numbers only";
    return false;
  }

  if (raw.length !== length) {
    otpError.value = "Enter a valid OTP";
    return false;
  }

  return true;
}

async function handleVerifyOtp(value?: unknown) {
  if (isVerifyingOtp.value) return;
  isVerifyingOtp.value = true;
  try {
    // `@finish` emits the OTP string, but `@click="handleVerifyOtp"` passes a MouseEvent.
    if (typeof value === "string") otp.value = value;

    emailError.value = "";
    if (!email.value.trim()) {
      emailError.value = "Email is required";
      return;
    }
    if (!/.+@.+\..+/.test(email.value)) {
      emailError.value = "Enter a valid email address";
      return;
    }

    if (!validateOtp(6)) return;

    const response = await passwordVerifyOTP({
      email: email.value,
      otp: otp.value,
    });

    snackbar.success(response?.message || "Verified");

    
    step.value = 3;
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to verify OTP";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.otp?.length) otpError.value = errors.otp[0];
    }

    if (!otpError.value) {
      otpError.value = message;
    }
  } finally {
    isVerifyingOtp.value = false;
  }
}
</script>

<style scoped>
.login-screen {
  height: calc(100vh - 250px);
  display: flex;
  align-items: center;
}

.login-card {
  width: 100%;
  height: calc(100vh - 170px);
  border-radius: 18px;
  padding: 26px 22px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.signup-steps {
  display: flex;
  align-items: center;
  gap: 10px;
}

.signup-steps__item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
}

.signup-steps__item.active {
  color: #0f172a;
}

.signup-steps__bubble {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 700;
}

.signup-steps__item.active .signup-steps__bubble {
  border-color: var(--color-theme, #e8612f);
}

.signup-steps__item.done .signup-steps__bubble {
  background: var(--color-theme, #e8612f);
  border-color: var(--color-theme, #e8612f);
  color: #fff;
}

.signup-steps__label {
  font-size: 13px;
  font-weight: 600;
}

.signup-steps__line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  border-radius: 999px;
}

.signup-steps__line.done {
  background: var(--color-theme, #e8612f);
}

.verified {
  text-align: center;
  padding: 6px 0 0;
  margin-top: 100px;
}

.verified__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.12);
  color: rgb(var(--v-theme-success));
  margin: 6px auto 10px;
}

.verified__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.verified__subtitle {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 14px;
}


.signup-text {
  text-align: center;
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 14px;
}

.signup-link {
  border: 0;
  background: transparent;
  color: var(--color-theme);
  font-size: inherit;
  font-weight: 700;
  padding: 0 0 0 4px;
}
</style>
